// ---------------------------------------------------------------------------
// Stage-order application: sort a channel's Stage (content-type) options by
// the admin-saved order from /api/v3/channel-settings.
//
// The saved order is CHANNEL-wide — one list for every product. A product's
// dropdown holds its own subset of the channel's types, so the sort must be
// robust to values the subset lacks, and to values the saved list has never
// heard of (a type added in code after the order was saved): listed values
// come first in list order, unlisted ones KEEP their declared relative order
// and follow after. Nothing is ever dropped.
// ---------------------------------------------------------------------------

export interface StageOptionLike {
  value: string;
}

/**
 * `options` in display order. `stageOrder` null/empty ⇒ the declared order,
 * untouched (the pre-feature behaviour, byte for byte).
 */
export function applyStageOrder<Option extends StageOptionLike>(
  options: Option[],
  stageOrder: string[] | null | undefined
): Option[] {
  if (!stageOrder || stageOrder.length === 0) return options;
  const rank = new Map(stageOrder.map((value, index) => [value, index]));
  // Stable partition, not a comparator sort: listed options by their rank,
  // unlisted options after, in their original relative order.
  const listed = options
    .filter((option) => rank.has(option.value))
    .sort((a, b) => (rank.get(a.value) as number) - (rank.get(b.value) as number));
  const unlisted = options.filter((option) => !rank.has(option.value));
  return [...listed, ...unlisted];
}

/**
 * The Stage value a channel opens on: the first saved entry the target product
 * actually offers, else `declaredDefault` (the DEFAULT_TYPES entry).
 *
 * The leading stage is the point of the saved order — during an EAP cycle a
 * user must LAND on EAP, not merely see it at the top of the dropdown. The
 * order is channel-wide while `available` is one product's subset (the
 * releases channel's RC carries no IntelliJ IDEA, for instance), so the first
 * saved entry is not always a valid landing type; taking it blindly would
 * route to a product/stage pair that has no template.
 */
export function resolveDefaultStage(
  stageOrder: string[] | null | undefined,
  available: string[],
  declaredDefault: string
): string {
  if (!stageOrder || stageOrder.length === 0) return declaredDefault;
  const offered = new Set(available);
  return stageOrder.find((value) => offered.has(value)) ?? declaredDefault;
}

/**
 * The stage a product opens on from the Product dropdown: the first saved
 * stage it offers, else the declared default when it offers that, else its
 * first selectable stage.
 */
export function productLandingStage(
  stageOrder: string[] | null | undefined,
  selectableTypes: string[],
  declaredDefault: string
): string {
  const declaredFallback = selectableTypes.includes(declaredDefault)
    ? declaredDefault
    : (selectableTypes[0] ?? declaredDefault);
  return resolveDefaultStage(stageOrder, selectableTypes, declaredFallback);
}

export interface StageTypeLike extends StageOptionLike {
  defaultLayout?: number;
}

export interface StageCorrectionInput {
  /** The channel's declared stages, or null for a channel that has none. */
  types: StageTypeLike[] | null | undefined;
  /** True only for a channel whose types are release stages. */
  typesAreStages?: boolean;
  /** The held product code. */
  product: string;
  /**
   * Every stage that has the product, a non-selectable one included: the
   * code's stages and the ones an admin put it on (libs/productOffer.ts
   * stagesWithProduct).
   */
  carriedTypes: string[];
  /** The held stage value. */
  type: string | null | undefined;
  /** The stages that may be offered in a selector, in declared order. */
  selectableTypes: string[];
  /** The channel's admin-saved stage order. */
  stageOrder: string[] | null | undefined;
  /** Truthy on a /scene/:id route. */
  savedSceneId?: string | null;
  /** True for the synthetic "All Products" entry. */
  isAllProducts?: boolean;
}

export interface StageCorrection {
  type: string;
  layout: number;
}

/**
 * The stage a (product, stage) pair must move to, or null when it may stand.
 *
 * A channel whose types are release stages keeps the product and the stage
 * independent — the Product dropdown offers the whole channel, so a route can
 * name a stage the product does not carry. The stage is what gives way: the
 * product is the choice the user made, and the legacy default-scene cascade
 * falls back to the stage map's FIRST entry for a product the stage does not
 * declare, which would quietly serve another product's scene.
 *
 * Returns null for a saved scene (it carries its own pair and names neither in
 * the URL, so a redirect would discard it), for "All Products", and for a
 * channel whose types are content formats — there the format is the meaningful
 * axis and the selector stays narrowed to it instead.
 */
export function resolveStageCorrection({
  types,
  typesAreStages,
  product,
  carriedTypes,
  type,
  selectableTypes,
  stageOrder,
  savedSceneId,
  isAllProducts,
}: StageCorrectionInput): StageCorrection | null {
  if (savedSceneId || isAllProducts || !typesAreStages || !type || !product) return null;

  // Membership is tested against EVERY stage the product carries, including a
  // non-selectable one: those stay valid for direct routes and saved scenes.
  if (carriedTypes.includes(type)) return null;

  // The fallback is drawn from the selectable stages only, and resolved
  // through the saved order — the same rule the landing URL and the product
  // switch apply.
  if (!selectableTypes.length) return null;
  const stage = resolveDefaultStage(stageOrder, selectableTypes, selectableTypes[0]);

  return {type: stage, layout: (types ?? []).find((t) => t.value === stage)?.defaultLayout ?? 1};
}
